// Échéances d'un dossier.
//
// C'est probablement l'écran qui compte le plus pour un avocat : manquer un délai
// de prescription n'est pas un désagrément, c'est une faute disciplinaire. Le
// calendrier n'est donc pas un agenda de confort — il fait partie de la défense.
//
// Le serveur ne voit que des DATES. Le titre, le lieu, le type (audience, délai
// de prescription…) et la criticité sont chiffrés sous la clé du dossier, partagée
// entre ses seuls participants.

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { DossierState } from '../crypto/mls-state';
import {
  createEvent,
  deleteEvent,
  listEvents,
  type Ctx,
  type EventDetails,
  type EventEntry,
} from './engine';

const message = (e: unknown) => (e instanceof Error ? e.message : String(e));

const TYPES: { value: EventDetails['eventType']; label: string }[] = [
  { value: 'audience', label: 'Audience' },
  { value: 'rdv_client', label: 'Rendez-vous client' },
  { value: 'echeance_procedurale', label: 'Échéance procédurale' },
  { value: 'delai_prescription', label: 'Délai de prescription' },
  { value: 'autre', label: 'Autre' },
];

const libelle = (t: EventDetails['eventType']) =>
  TYPES.find((x) => x.value === t)?.label ?? t;

/** Une échéance passée n'alerte plus ; une échéance proche, si. */
function urgence(startsAt: string): 'passe' | 'imminent' | 'a_venir' {
  const jours = (new Date(startsAt).getTime() - Date.now()) / 86_400_000;
  if (jours < 0) return 'passe';
  return jours <= 7 ? 'imminent' : 'a_venir';
}

const dateLongue = (iso: string) =>
  new Date(iso).toLocaleString('fr-FR', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const field =
  'mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm ' +
  'focus:border-blue-600 focus:outline-none';

export function Calendar({
  ctx,
  dossierId,
  state,
}: {
  ctx: Ctx;
  dossierId: string;
  state: DossierState;
}) {
  const [events, setEvents] = useState<EventEntry[] | null>(null);
  const [ouvert, setOuvert] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [startsAt, setStartsAt] = useState('');
  const [title, setTitle] = useState('');
  const [location, setLocation] = useState('');
  const [eventType, setEventType] =
    useState<EventDetails['eventType']>('audience');
  const [isCritical, setIsCritical] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      setEvents(await listEvents(ctx, dossierId, state));
    } catch (e) {
      setError(message(e));
    }
  }, [ctx, dossierId, state]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function onCreate(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await createEvent(ctx, dossierId, state, new Date(startsAt), {
        title: title.trim(),
        location: location.trim() || undefined,
        eventType,
        isCritical,
      });
      setStartsAt('');
      setTitle('');
      setLocation('');
      setIsCritical(false);
      setOuvert(false);
      await refresh();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }

  async function onDelete(id: string) {
    setBusy(true);
    setError(null);
    try {
      await deleteEvent(ctx, dossierId, id);
      await refresh();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mt-8 border-t border-slate-200 pt-4" data-testid="calendar">
      <div className="flex items-center gap-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Échéances
        </h3>
        <button
          type="button"
          onClick={() => setOuvert((v) => !v)}
          disabled={!state.dossierKey}
          data-testid="toggle-new-event"
          className="ml-auto text-xs text-blue-900 underline hover:text-blue-700 disabled:opacity-40"
        >
          {ouvert ? 'Annuler' : 'Poser une échéance'}
        </button>
      </div>

      {!state.dossierKey && (
        <p className="mt-2 text-xs text-amber-700" data-testid="calendar-no-key">
          Clé du dossier pas encore reçue : les échéances resteront illisibles tant
          qu&apos;un membre ne te l&apos;aura pas transmise par le groupe chiffré.
        </p>
      )}

      {error && (
        <p data-testid="calendar-error" className="mt-2 text-sm text-red-600">
          {error}
        </p>
      )}

      {ouvert && (
        <form
          onSubmit={onCreate}
          className="mt-3 rounded border border-slate-200 p-3"
          data-testid="new-event"
        >
          <label className="block text-sm font-medium text-slate-700">
            Intitulé
            <input
              className={field}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="ex. Audience de mise en état"
              data-testid="event-title"
            />
          </label>

          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <label className="block text-sm font-medium text-slate-700">
              Date et heure
              <input
                className={field}
                type="datetime-local"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
                data-testid="event-date"
              />
            </label>

            <label className="block text-sm font-medium text-slate-700">
              Nature
              <select
                className={field}
                value={eventType}
                onChange={(e) =>
                  setEventType(e.target.value as EventDetails['eventType'])
                }
                data-testid="event-type"
              >
                {TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="mt-3 block text-sm font-medium text-slate-700">
            Lieu (facultatif)
            <input
              className={field}
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="ex. TJ Paris, chambre 3"
              data-testid="event-location"
            />
          </label>

          <label className="mt-3 flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={isCritical}
              onChange={(e) => setIsCritical(e.target.checked)}
              data-testid="event-critical"
            />
            Échéance critique (la manquer engage ma responsabilité)
          </label>

          <button
            type="submit"
            disabled={busy || !title.trim() || !startsAt}
            data-testid="create-event"
            className="mt-3 w-full rounded bg-blue-900 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-50"
          >
            {busy ? 'Chiffrement…' : 'Poser l’échéance'}
          </button>

          <p className="mt-2 text-[11px] text-slate-400">
            Le serveur n&apos;apprendra qu&apos;une chose : ce dossier a{' '}
            <em>quelque chose</em> ce jour-là. Ni quoi, ni où, ni si c&apos;est
            grave.
          </p>
        </form>
      )}

      <ul className="mt-3 space-y-2" data-testid="event-list">
        {events?.length === 0 && (
          <li className="text-sm text-slate-400">Aucune échéance.</li>
        )}
        {events?.map((e) => {
          const u = urgence(e.startsAt);
          const critique = e.details?.isCritical ?? false;
          return (
            <li
              key={e.id}
              data-testid="event-item"
              className={`rounded border p-3 ${
                u === 'passe'
                  ? 'border-slate-200 bg-slate-50 opacity-60'
                  : critique && u === 'imminent'
                    ? 'border-red-300 bg-red-50'
                    : critique
                      ? 'border-amber-300 bg-amber-50'
                      : 'border-slate-200'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800">
                    {/* Sans la clé du dossier, il ne reste que la date — ce que le
                        serveur voit, lui aussi. */}
                    {e.details?.title ?? (
                      <em className="text-slate-400">
                        échéance illisible sur cet appareil
                      </em>
                    )}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {dateLongue(e.startsAt)}
                    {e.details && ` · ${libelle(e.details.eventType)}`}
                    {e.details?.location && ` · ${e.details.location}`}
                  </p>
                  {critique && u !== 'passe' && (
                    <p
                      data-testid="event-critical-flag"
                      className={`mt-1 text-xs font-medium ${
                        u === 'imminent' ? 'text-red-700' : 'text-amber-700'
                      }`}
                    >
                      {u === 'imminent'
                        ? 'Critique — dans moins de 7 jours'
                        : 'Critique'}
                    </p>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => onDelete(e.id)}
                  disabled={busy}
                  data-testid="delete-event"
                  className="ml-auto shrink-0 text-xs text-slate-400 underline hover:text-red-600 disabled:opacity-50"
                >
                  Retirer
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
